import { useEffect, useState, useMemo, useCallback } from 'react';
import { Event } from 'nostr-tools';
import { getNostrPool } from '../lib/nostrPool';

/**
 * All Feed Hook - Fetch all Turkish content from Nostr network
 * No topic filtering, just language-based content discovery
 */
import { RELAYS } from '../config/constants';
import { useGlobalModeration } from './useGlobalModeration';

/**
 * All Feed Hook - Fetch all Turkish content from Nostr network
 * No topic filtering, just language-based content discovery
 */
export const useAllFeed = (limit: number = 20, language: 'tr' | 'all' = 'tr') => {
    const [allEvents, setAllEvents] = useState<Event[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLinking, setIsLinking] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const pool = getNostrPool();
    const { blockedEventIds, blockedPubkeys } = useGlobalModeration();

    // Refetch function
    const refetch = useCallback(() => {
        setRefreshKey(prev => prev + 1);
    }, []);

    const loadMore = useCallback(async () => {
        if (isLinking || isLoading) return;

        const oldestEvent = allEvents[allEvents.length - 1];
        if (!oldestEvent) return;

        setIsLinking(true);

        const turkishHashtags = [
            'türkiye', 'turkiye', 'türkçe', 'turkce',
            'istanbul', 'ankara', 'izmir', 'tr',
            'mavililer', 'mavililersözlük'
        ];

        const filter: any = {
            kinds: [1],
            until: oldestEvent.created_at - 1,
            limit: limit * 4 // Fetch more to filter more aggressively
        };

        if (language === 'tr') {
            filter['#t'] = turkishHashtags;
        }

        const moreEvents: Event[] = [];
        const sub = pool.subscribeMany(RELAYS, filter, {
            onevent(event: Event) {
                if (language === 'tr') {
                    // EXCLUDE community posts from general Yerel feed
                    const hasCommunityTag = event.tags.some(t =>
                        t[0] === 't' && ['mavililer', 'mavililersözlük', 'mavililersozluk'].includes(t[1].toLowerCase())
                    );
                    if (hasCommunityTag) return;

                    // 1. Check NIP-32 language tag
                    const languageTag = event.tags.find(t => t[0] === 'l' && t[2] === 'ISO-639-1');
                    if (languageTag) {
                        const lang = languageTag[1];
                        if (lang !== 'tr' && lang !== 'tur') {
                            return;
                        }
                    } else {
                        // 2. Fallback: Check for Turkish-specific characters OR common words
                        const turkishChars = /[çğışöüÇĞİŞÖÜ]/;
                        const commonWords = /\b(ve|bir|bu|da|de|için|çok|o|ne|mi|ki|olan|bu|şu|her)\b/i;

                        if (!turkishChars.test(event.content) && !commonWords.test(event.content)) {
                            return;
                        }
                    }
                }
                moreEvents.push(event);
            },
            oneose() {
                if (moreEvents.length > 0) {
                    setAllEvents(prev => {
                        const newEvents = moreEvents.filter(ne => !prev.some(pe => pe.id === ne.id));
                        return [...prev, ...newEvents].sort((a, b) => b.created_at - a.created_at);
                    });
                }
                setIsLinking(false);
                sub.close();
            }
        });

        setTimeout(() => {
            setIsLinking(false);
            sub.close();
        }, 5000);
    }, [allEvents, isLinking, isLoading, limit, pool, language]);

    useEffect(() => {
        setIsLoading(true);
        setAllEvents([]);

        // Turkish hashtags - stricter list
        const turkishHashtags = [
            'türkiye', 'turkiye', 'türkçe', 'turkce',
            'istanbul', 'ankara', 'izmir', 'tr',
            'mavililer', 'mavililersözlük'
        ];

        const filter: any = {
            kinds: [1], // Only short-form notes
            limit: limit * 10, // Get much more to allow for selective filtering
        };

        if (language === 'tr') {
            filter['#t'] = turkishHashtags;
        }

        // Safety timeout
        const timeout = setTimeout(() => {
            setIsLoading(false);
        }, 5000);

        const sub = pool.subscribeMany(RELAYS, filter, {
            onevent(event: Event) {
                clearTimeout(timeout);
                // Check NIP-32 language tag if in Turkish mode
                if (language === 'tr') {
                    // EXCLUDE community posts from general Yerel feed
                    const hasCommunityTag = event.tags.some(t =>
                        t[0] === 't' && ['mavililer', 'mavililersözlük', 'mavililersozluk'].includes(t[1].toLowerCase())
                    );
                    if (hasCommunityTag) return;

                    const languageTag = event.tags.find(t => t[0] === 'l' && t[2] === 'ISO-639-1');
                    if (languageTag) {
                        const lang = languageTag[1];
                        if (lang !== 'tr' && lang !== 'tur') {
                            return; // Not Turkish
                        }
                    } else {
                        // Regex fallback: check for frequent Turkish characters
                        const turkishChars = /[çğışöüÇĞİŞÖÜ]/;
                        const commonWords = /\b(ve|bir|bu|da|de|için|çok|o|ne|mi|ki|olan|bu|şu|her)\b/i;

                        if (!turkishChars.test(event.content) && !commonWords.test(event.content)) {
                            return;
                        }
                    }
                }

                setAllEvents((prev) => {
                    const exists = prev.some((e) => e.id === event.id);
                    if (exists) return prev;
                    return [...prev, event]
                        .sort((a, b) => b.created_at - a.created_at)
                        .slice(0, limit * 20);
                });
            },
            oneose() {
                clearTimeout(timeout);
                setIsLoading(false);
            },
        });

        return () => {
            clearTimeout(timeout);
            sub.close();
        };
    }, [limit, pool, refreshKey, language]);

    // REACTIVE FILTERING PIPELINE
    const filteredEvents = useMemo(() => {
        return allEvents
            .filter(e => {
                if (blockedEventIds.has(e.id)) return false;
                if (blockedPubkeys.has(e.pubkey)) return false;
                return true;
            })
            .slice(0, limit);
    }, [allEvents, blockedEventIds, blockedPubkeys, limit]);

    return { allEvents: filteredEvents, isLoading, isLinking, loadMore, refetch };
};
